import { createSlice, createAsyncThunk, } from '@reduxjs/toolkit';
import { getApiClient } from '../../utils/apiClient';
// Async thunks
const getStripeInfo = createAsyncThunk('payment/getStripeInfo', async (_, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/stripe-info',
            method: 'get',
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const stopSubscription = createAsyncThunk('payment/stopSubscription', async ({ onSuccess } = {}, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/cancel-subscription',
            method: 'post',
        });
        onSuccess?.();
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const createCheckoutSession = createAsyncThunk('payment/createCheckoutSession', async ({ priceId, isSubscription, callback, }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            method: 'post',
            url: '/api/create-checkout-session',
            data: {
                priceId,
                isSubscription,
            },
        });
        callback(response.data.redirectUrl);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// Initial state
const initialState = {
    stripePlans: [
        { id: 'monthly', amount: 1.99, title: 'Monthly' },
        { id: 'yearly', amount: 12, title: 'Yearly' },
    ],
    stripeKeyPub: '',
    loading: false,
    stopSubscriptionLoading: false,
    checkoutSessionLoading: false,
    error: null,
};
// Payment slice
const paymentSlice = createSlice({
    name: 'payment',
    initialState,
    reducers: {
        // Clear error
        clearError: (state) => {
            state.error = null;
        },
        // Set stripe plans directly
        setStripePlans: (state, action) => {
            state.stripePlans = action.payload.stripePlans;
        },
        // Set stripe public key directly
        setStripeKeyPub: (state, action) => {
            state.stripeKeyPub = action.payload.stripeKeyPub;
        },
        // Set stripe info (both plans and key)
        setStripeInfo: (state, action) => {
            state.stripePlans = action.payload.stripePlans;
            state.stripeKeyPub = action.payload.stripeKeyPub;
        },
        // Reset payment state
        resetPaymentState: () => initialState,
    },
    extraReducers: (builder) => {
        // Get stripe info
        builder
            .addCase(getStripeInfo.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(getStripeInfo.fulfilled, (state, action) => {
            state.loading = false;
            state.stripePlans =
                action.payload.stripePlans || state.stripePlans;
            state.stripeKeyPub = action.payload.stripeKeyPub || '';
            state.error = null;
        })
            .addCase(getStripeInfo.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // Stop subscription
        builder
            .addCase(stopSubscription.pending, (state) => {
            state.stopSubscriptionLoading = true;
            state.error = null;
        })
            .addCase(stopSubscription.fulfilled, (state) => {
            state.stopSubscriptionLoading = false;
            state.error = null;
        })
            .addCase(stopSubscription.rejected, (state, action) => {
            state.stopSubscriptionLoading = false;
            state.error = action.payload;
        });
        // Create checkout session
        builder
            .addCase(createCheckoutSession.pending, (state) => {
            state.checkoutSessionLoading = true;
            state.error = null;
        })
            .addCase(createCheckoutSession.fulfilled, (state) => {
            state.checkoutSessionLoading = false;
            state.error = null;
        })
            .addCase(createCheckoutSession.rejected, (state, action) => {
            state.checkoutSessionLoading = false;
            state.error = action.payload;
        });
    },
});
// Selectors
const selectPaymentState = (state) => state.payment;
const selectStripePlans = (state) => state.payment.stripePlans;
const selectStripeKeyPub = (state) => state.payment.stripeKeyPub;
const selectPaymentLoading = (state) => state.payment.loading;
const selectStopSubscriptionLoading = (state) => state.payment.stopSubscriptionLoading;
const selectCheckoutSessionLoading = (state) => state.payment.checkoutSessionLoading;
const selectPaymentError = (state) => state.payment.error;
const selectIsPaymentLoading = (state) => state.payment.loading ||
    state.payment.stopSubscriptionLoading ||
    state.payment.checkoutSessionLoading;
// Get plan by ID selector factory
const selectPlanById = (planId) => (state) => state.payment.stripePlans.find((plan) => plan.id === planId);
// Get monthly plan selector
const selectMonthlyPlan = (state) => state.payment.stripePlans.find((plan) => plan.id === 'monthly');
// Get yearly plan selector
const selectYearlyPlan = (state) => state.payment.stripePlans.find((plan) => plan.id === 'yearly');
// Grouped exports for better organization
export const paymentActions = {
    ...paymentSlice.actions,
    // Async actions
    getStripeInfo,
    stopSubscription,
    createCheckoutSession,
};
export const paymentSelectors = {
    selectPaymentState,
    selectStripePlans,
    selectStripeKeyPub,
    selectPaymentLoading,
    selectStopSubscriptionLoading,
    selectCheckoutSessionLoading,
    selectPaymentError,
    selectIsPaymentLoading,
    selectPlanById,
    selectMonthlyPlan,
    selectYearlyPlan,
};
// Export reducer
export default paymentSlice.reducer;
